import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import axiosRetry, { isRetryableError } from "axios-retry";
import * as Sentry from "@sentry/astro";

import { logger } from "./services/logger";
import { logout, refreshAuthBrowser, refreshAuthServer } from "./features/auth/services/api";
import { mergeNewCookies } from "./utils/merge-cookies";
import { isBrowser } from "./utils/platform";
import { ACCESS_TOKEN_COOKIE, REFRESH_TOKEN_COOKIE } from "./features/auth/middleware";
import { universalStorage } from "./utils/universal-storage";
import { MARKETING_URL_PARAMS } from "./services/consts";
import type { Platform } from "./services/types";
import type { SharedStorage } from "./env";

const instance = axios.create();
instance.defaults.baseURL = import.meta.env.PUBLIC_API_BASE_URL as string;
instance.defaults.withCredentials = true;
const RETRY_AMOUNT = 3;
axiosRetry(instance, {
  retries: RETRY_AMOUNT,
  retryDelay: axiosRetry.exponentialDelay,
  onRetry: (retryCount, error, requestConfig) => {
    logger.warn("API Call Retry", { error, retryCount, requestConfig });
    if (RETRY_AMOUNT === retryCount) {
      logger.error("API Call Retry Failed", { error, retryCount, requestConfig });
    }
  },
});

instance.interceptors.request.use(async (config) => {
  onRequestSetServerAuthCookies(config);
  onRequestSetMarketingUrlParams(config);
  onRequestSetFbTrackingParams(config);
  onRequestSetDevice(config);
  onRequestSetClientIp(config);

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (e) => {
    if (axios.isCancel(e)) {
      const error = { title: "Cancel request", code: "cancel_request" };
      logger.error("Cancel API request", error);
      return Promise.reject(error);
    }
    try {
      if (isAxiosError(e)) {
        const newRequest = await handleAuthRefresh(e);
        if (newRequest) return newRequest;

        sendErrorsToSentry(e);
      }

      if (!isRetryableError(e)) {
        logger.warn("API Call Non-Retryable Error", e);
      }

      return Promise.reject(e);
    } catch (error) {
      logger.error("Unhandled axios response interceptor error", { error, e });

      return Promise.reject(e);
    }
  }
);

function onRequestSetMarketingUrlParams(config: InternalAxiosRequestConfig) {
  MARKETING_URL_PARAMS.forEach((param) => {
    const value = universalStorage.get(param);
    if (value) {
      config.headers[param] = value;
    }
  });
}

function onRequestSetFbTrackingParams(config: InternalAxiosRequestConfig) {
  const FB_PARAM_KEYS: Array<keyof SharedStorage> = ["fbclid", "fbp"];

  FB_PARAM_KEYS.forEach((param) => {
    const value = universalStorage.get(param);
    if (value) {
      config.headers[param] = value;
    }
  });
}

async function onRequestSetDevice(config: InternalAxiosRequestConfig) {
  const userDeviceMap: Record<Platform, "MOBILE" | "DESKTOP"> = {
    desktop: "DESKTOP",
    mobile: "MOBILE",
  };

  const device = universalStorage.get("mobile_or_desktop");
  if (device) config.headers["device"] = userDeviceMap[device];
}

async function onRequestSetClientIp(config: InternalAxiosRequestConfig) {
  const clientIp = universalStorage.get("client_ip");

  if (clientIp) config.headers["x-imagen-client-ip"] = clientIp;
}

async function onRequestSetServerAuthCookies(config: InternalAxiosRequestConfig) {
  if (!isBrowser()) {
    const context = global._contextStorage?.getStore();

    const existingCookies = config.headers["Cookie"] || "";
    const cookies = mergeNewCookies(existingCookies, {
      [REFRESH_TOKEN_COOKIE]: context?.locals.auth?.refresh_token || "",
      [ACCESS_TOKEN_COOKIE]: context?.locals.auth?.access_token || "",
    });

    config.headers["Cookie"] = cookies;
  }
}

async function handleAuthRefresh(e: AxiosError) {
  if (!e.config) {
    return;
  }

  const unAuthedResponse = e.response?.status === 401;
  if (unAuthedResponse) {
    try {
      if (isBrowser()) {
        await refreshAuthBrowser();
        return instance.request(e.config);
      } else {
        const context = global._contextStorage?.getStore();
        if (!context?.locals.auth?.refresh_token) return;

        const { access_token } = await refreshAuthServer(context.locals.auth.refresh_token);
        context.locals.auth.access_token = access_token;

        return instance.request(e.config);
      }
    } catch (error) {
      logger.error("handleAuthRefresh failed to refresh", error);
      await logout();
    }
  }
}

function sendErrorsToSentry(e: AxiosError) {
  if (e.status !== 401) {
    const url = String(e.config?.url);
    const statusCode = String(e.response?.status);
    const method = String(e.config?.method);

    Sentry.captureMessage(`${method.toUpperCase()} ${url} - Request failed with status code ${statusCode}`, (scope) => {
      scope.setLevel("error");
      scope.setFingerprint([method, url, statusCode]);

      return scope.setContext("request-extra-data", {
        url,
        request_params: e.config?.params,
        request_data: e.config?.data,
        response_data: e.response?.data,
      });
    });
  }
}

export const api = (path: string) => {
  return `${import.meta.env.PUBLIC_API_BASE_URL}${path}`;
};

export function isAxiosError<ResponseType>(error: unknown): error is AxiosError<ResponseType> {
  return axios.isAxiosError(error);
}

interface ImagenError {
  error: {
    message?: string;
    type?: string;
  };
}
export function isImagenError(error: unknown): error is AxiosError<ImagenError> {
  return !!(isAxiosError<ImagenError>(error) && error.response?.data.error);
}

export default instance;
